import type { PlacementBand } from '@nelnela/contracts';

/**
 * The placement wizard's decisions (M10-iii), kept out of the screen so they can be tested.
 *
 * The web suite is node with no DOM, so a component's behaviour is unreachable to it (M6c's known
 * gap). Everything here is a pure function over the run's state and `Placement.tsx` is wiring —
 * the same split `lib/cards.ts` and `lib/journal.ts` already make.
 *
 * THE CLIENT IS THE ONLY THING THAT REMEMBERS A RUN. The routes are stateless by design (no
 * `placement_session` table, and 0013 deliberately did not add one), so this module holds the
 * whole transcript and sends all of it on every call. Two consequences the screen depends on:
 * quitting is free and instant — there is nothing to tell the server — and the trail below can be
 * drawn from local state without a round trip.
 */

/** A band being asked right now: the server's deal, plus the answers given so far. */
export interface Deal {
  level: number;
  items: ReadonlyArray<{ itemId: string; display: string }>;
  /** one entry per item answered, in deal order */
  answers: boolean[];
}

export interface Run {
  /** bands already completed, oldest first — the transcript both routes are given */
  done: PlacementBand[];
  /** the band on screen, or null while a deal is in flight or the climb has ended */
  deal: Deal | null;
}

export const emptyRun = (): Run => ({ done: [], deal: null });

/** The word on screen, or null when there is nothing to ask. */
export function currentWord(run: Run): string | null {
  const d = run.deal;
  if (!d) return null;
  return d.items[d.answers.length]?.display ?? null;
}

/**
 * Record one yes/no.
 *
 * When the answer completes the band, the deal is FOLDED into `done` and cleared — so a finished
 * band is transcript and an unfinished one is not, which is the same line the CLI draws when it
 * refuses to `--apply` a run abandoned mid-band. The caller asks for the next deal when
 * `run.deal` is null and `isComplete` is false.
 */
export function answer(run: Run, said: boolean): Run {
  const d = run.deal;
  if (!d || d.answers.length >= d.items.length) return run;
  const answers = [...d.answers, said];
  if (answers.length < d.items.length) return { ...run, deal: { ...d, answers } };
  return { done: [...run.done, { level: d.level, answers }], deal: null };
}

/** The transcript to send: completed bands only. A band in progress is not evidence. */
export const transcript = (run: Run): PlacementBand[] => run.done;

/**
 * The trail the climb draws, and the one piece of α that is a rule rather than a decoration.
 *
 * "climbed bands settle as filled steps, the current band breathes, exactly ONE dashed ghost ahead
 * — the trail never promises a length it can't know". That last clause is the whole point: the
 * climb's length is not knowable in advance, because it ends when a band falls under threshold or
 * the catalog thins. A progress bar would have to invent a denominator, and inventing one is
 * lying about how long this takes.
 */
export type Step = { level: number; state: 'settled' | 'current' | 'ghost' };

export function trail(run: Run, current: number | null): Step[] {
  const steps: Step[] = run.done.map((b) => ({ level: b.level, state: 'settled' as const }));
  if (current === null) return steps;
  steps.push({ level: current, state: 'current' });
  // Exactly one ghost, and only when there is somewhere for it to point.
  if (current + 10 <= 100) steps.push({ level: current + 10, state: 'ghost' });
  return steps;
}

/**
 * The trail as the CLIMB draws it, which is not quite `trail` — because between two bands there is
 * a network round trip, and during it `run.deal` is null.
 *
 * Drawing `trail(run, null)` in that gap makes the current step and its ghost vanish and come
 * back, which on a phone is a visible stutter in the one piece of motion this screen has. So the
 * gap keeps a ghost: the honest picture is "we do not know what is next yet", and a dashed
 * outline is exactly that. The band just finished has already moved into `done`, so it is drawn
 * settled and is not drawn twice.
 */
export function climbSteps(run: Run): Step[] {
  if (run.deal) return trail(run, run.deal.level);
  const settled = trail(run, null);
  const highest = run.done.reduce((m, b) => Math.max(m, b.level), 0);
  const ahead = highest === 0 ? 1 : highest + 10;
  return ahead <= 100 ? [...settled, { level: ahead, state: 'ghost' as const }] : settled;
}

/** "LEVELS 21–30" — the band chip, which doubles as the "where am I" answer. */
export const bandLabel = (level: number): string => `LEVELS ${level}–${level + 9}`;

/** How far through the current band, for the row of dots. Null when nothing is being asked. */
export function dots(run: Run): { total: number; filled: number } | null {
  const d = run.deal;
  return d ? { total: d.items.length, filled: d.answers.length } : null;
}

/**
 * "around levels 21–30" — how the result is spoken.
 *
 * A band, never a number, and never a confidence meter: provisionality lives in the copy ("for
 * now", "it moves as you do"), which is α's call and the honest one. A percentage next to an
 * estimate drawn from at most a hundred yes/no answers would claim a precision it does not have.
 */
export const edgeLabel = (band: { min: number; max: number } | null): string | null =>
  band ? `around levels ${band.min}–${band.max}` : null;
